import { OnSmimeAttachmentCreatedCallback } from './createSmimeAttachmentViaSmimeQueueManager';
import removeDataHeaderInfoFromContent from './removeDataHeaderInfoFromContent';
import { logUsage } from 'owa-analytics';
import { PerformanceDatapoint } from 'owa-analytics';
import updateSmimeAttachment from 'owa-attachment-full-data/lib/actions/updateSmimeAttachment';
import isAttachmentPartOfItem from 'owa-attachment-full-data/lib/utils/isAttachmentPartOfItem';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { AttachmentId, AttachmentWellViewState } from 'owa-attachment-well-data';
import removeAttachmentFromWell from 'owa-attachment-well-data/lib/actions/removeAttachmentFromWell';
import { OnAttachmentDeletedCallback } from 'owa-attachment-well-data/lib/types/AttachmentDeleteQueueItem';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import decode from 'owa-encoding-utils/lib/utils/base64/decode';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import loc, { format } from 'owa-localize';
import { smimeComposeHasCloudAttachments } from 'owa-locstrings/lib/strings/smimecomposehascloudattachments.locstring.json';
import ItemAttachment from 'owa-service/lib/contract/ItemAttachment';
import ItemIdAttachment from 'owa-service/lib/contract/ItemIdAttachment';
import attachmentId from 'owa-service/lib/factory/attachmentId';
import getMimeOperation from 'owa-service/lib/operation/getMimeOperation';
import { getIdWithSmimePrefix } from 'owa-smime-adapter/lib/utils/smimeUtils';
import fetchAttachmentContent from 'owa-smime/lib/services/fetchAttachmentContent';
import { trace } from 'owa-trace';
import {
    WarningAttachmentsCanNotBeDownloaded,
    WarningAttachmentsCanNotBeDownloadedMultipleFiles,
} from './SmimeAttachmentQueueManager.locstring.json';
import {
    AttachmentFullViewState,
    ActionType,
    AttachmentState,
    InlineAttachmentStatus,
    AttachmentStateType,
    setOngoingActionAndActionMessage,
    ActionMessageId,
    InlineAttachmentState,
} from 'owa-attachment-full-data';
import {
    getTypeOfAttachment,
    TypeOfAttachment,
    AttachmentModel,
    SmimeAttachmentType,
    AttachmentType,
    isSmimeAttachmentType,
} from 'owa-attachment-model-store';
import {
    ITEM_ATTACHMENT_TYPE,
    ITEM_ID_ATTACHMENT_TYPE,
    FILE_ATTACHMENT_TYPE,
} from 'owa-attachment-model-store/lib/utils/attachmentConstants';

const UNSUPPORTED_REFERENCE_ATTACHMENT_ERROR_NAME = 'S/MIME: Unsupported reference attachment';
const GENERIC_ERROR_NAME = 'S/MIME: Unsupported attachment type';

interface SmimeAttachmentDownloadQueueItem {
    attachmentState: AttachmentState;
    attachment: AttachmentModel;
    onAttachmentDownloaded?: OnSmimeAttachmentCreatedCallback;
}

interface SmimeAttachmentDeleteQueueItem {
    attachmentState: AttachmentState;
    onAttachmentDeleted?: OnAttachmentDeletedCallback;
}

const enum AttachmentDownloadFailureReason {
    GenericError,
    UnsupportedReferenceAttachment,
}

interface AttachmentDownloadItemFailure {
    item: SmimeAttachmentDownloadQueueItem;
    reason: AttachmentDownloadFailureReason;
}

export interface SmimeAttachmentQueueManagerInterface {
    onFinishPromise: Promise<ClientAttachmentId>;

    enqueueSmimeAttachmentDownload(
        attachments: AttachmentState[],
        onAttachmentDownloaded?: OnSmimeAttachmentCreatedCallback
    );

    deleteSmimeAttachment(
        attachmentViewState: AttachmentState,
        onAttachmentDeleted?: OnAttachmentDeletedCallback
    );
}

export function getSmimeAttachmentQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    infoBarRemoveHandler: (infoBarMessageIds: string[]) => void
): SmimeAttachmentQueueManagerInterface {
    return new SmimeAttachmentQueueManager(
        attachmentWellViewState,
        parentItemId,
        infoBarAddHandler,
        infoBarRemoveHandler
    );
}
export class SmimeAttachmentQueueManager implements SmimeAttachmentQueueManagerInterface {
    // Push during enqueue and pop during process attachments
    private downloadQueue: SmimeAttachmentDownloadQueueItem[];
    // Download errors are stacked here, once download queue is empty call finishProcess and show infobar
    private downloadFailures: AttachmentDownloadItemFailure[];
    private deleteQueue: SmimeAttachmentDeleteQueueItem[];
    private isInProgress: boolean;
    private currentDownloadItem: SmimeAttachmentDownloadQueueItem;
    // Record performance datapoint; says how much time queue took to process all attachments
    private datapoint: PerformanceDatapoint;
    // Called once all the attachments have finished downloading with attachmentId of the last attachment processed
    private onFinish: (attachmentId: ClientAttachmentId) => void;
    // Resolved when onFinish is called.
    public onFinishPromise: Promise<ClientAttachmentId>;

    /**
     * The attachment queue manager which is responsible for managing creation and deletion of attachments in an item.
     * @param attachmentWellViewState The attachment well view state for parent item.
     * @param parentItemId The parent item id.
     * @param infoBarAddHandler A function to add message to info bar.
     * @param infoBarRemoveHandler A function to remove message from info bar.
     */
    constructor(
        private attachmentWellViewState: AttachmentWellViewState,
        private parentItemId: ClientItemId,
        private infoBarAddHandler: (
            messageId: string,
            infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
        ) => void,
        private infoBarRemoveHandler: (infoBarMessageIds: string[]) => void
    ) {
        this.downloadQueue = [];
        this.downloadFailures = [];
        this.deleteQueue = [];
        this.onFinishPromise = new Promise<ClientAttachmentId>((resolve, reject) => {
            // We will resolve when finishing process.
            this.onFinish = function(attachmentId: ClientAttachmentId) {
                resolve(attachmentId);
            };
        });

        // If no handlers are provided then we create empty handlers
        this.infoBarAddHandler = this.infoBarAddHandler || (() => {});
        this.infoBarRemoveHandler = this.infoBarRemoveHandler || (() => {});
    }

    /**
     * Adds attachment to download queue
     * @param attachments Attachment to be downloaded
     * @param onAttachmentDownloaded Callback which will run on successful attachment download
     */
    enqueueSmimeAttachmentDownload(
        attachments: AttachmentState[],
        onAttachmentDownloaded: OnSmimeAttachmentCreatedCallback = null
    ) {
        attachments.map(attachment => {
            const attachmentModel = getAttachment(attachment.attachmentId);
            const downloadQueueItem: SmimeAttachmentDownloadQueueItem = {
                attachmentState: attachment,
                attachment: attachmentModel,
                onAttachmentDownloaded: onAttachmentDownloaded,
            };

            this.downloadQueue.push(downloadQueueItem);
        });
        this.startProcessIfNotStarted();
    }

    /**
     * Removes attachment from well, aborts download of attachment
     * @param attachmentState Attachment to be deleted
     * @param onAttachmentDeleted Callback which will run once attachment is removed from well.
     */
    deleteSmimeAttachment(
        attachmentState: AttachmentState,
        onAttachmentDeleted?: OnAttachmentDeletedCallback
    ) {
        const isInline = attachmentState.attachmentType === AttachmentStateType.InlineAttachment;
        const isAttachmentPendingDownload = isInline
            ? (<InlineAttachmentState>attachmentState).status === InlineAttachmentStatus.Initialized
            : (<AttachmentFullViewState>attachmentState).ongoingAction === ActionType.None;

        if (!isInline) {
            setOngoingActionAndActionMessage(
                attachmentState as AttachmentFullViewState,
                ActionType.Delete,
                ActionMessageId.DeleteInProgress
            );
        }

        if (
            isAttachmentPendingDownload &&
            this.currentDownloadItem &&
            this.currentDownloadItem.attachmentState.attachmentId !== attachmentState.attachmentId
        ) {
            // In download queue, not downloading
            trace.info(
                'Attachment: Removing attachment: TempId= ' +
                    attachmentState.attachmentId +
                    ' from S/MIME download queue'
            );
            this.removeRelatedItemFromDownloadQueue(attachmentState, this.downloadQueue);
        }
        this.deleteQueue.push({
            attachmentState,
            onAttachmentDeleted,
        });
        this.startProcessIfNotStarted();
    }
    startProcessIfNotStarted() {
        if (!this.isInProgress) {
            this.datapoint = new PerformanceDatapoint('SmimeAttachmentQueueManager');
            this.isInProgress = true;
            this.processNextAttachment();
        }
    }

    async processNextAttachment() {
        if (this.deleteQueue.length != 0) {
            const { attachmentState, onAttachmentDeleted } = this.deleteQueue.shift();
            const attachmentModel = getAttachment(attachmentState.attachmentId);

            removeAttachmentFromWell(this.attachmentWellViewState, attachmentState);

            onAttachmentDeleted?.(
                this.parentItemId,
                attachmentModel.model.AttachmentId as ClientAttachmentId,
                attachmentModel.model
            );
            this.processNextAttachment();
        } else if (this.downloadQueue.length !== 0) {
            this.currentDownloadItem = this.downloadQueue.shift();
            const {
                attachmentState,
                attachment,
                onAttachmentDownloaded,
            } = this.currentDownloadItem;

            const attachmentType = getTypeOfAttachment(attachment.model);
            const smimeAttachment: SmimeAttachmentType = attachment.model;

            let attachType: AttachmentType;
            try {
                const attachmentIdToDownload: ClientAttachmentId = this.getAttachmentId(
                    attachment.model,
                    this.parentItemId
                );

                attachType = { ...smimeAttachment };
                if (attachmentType === TypeOfAttachment.Mail) {
                    const itemAttachment = smimeAttachment as ItemAttachment;
                    /**
                     * Don't download the attachment if MimeContent is present.
                     * This will happen when we switch from S/MIME to non-S/MIME and back to S/MIME again
                     */
                    if (itemAttachment.Item?.MimeContent) {
                        (<SmimeAttachmentType>attachType).MimeContent = decode(
                            itemAttachment.Item.MimeContent.Value
                        );
                    } else if (!smimeAttachment.MimeContent) {
                        // Download the attachment.
                        const mimeContent = attachmentIdToDownload?.Id
                            ? await fetchAttachmentContent(attachmentIdToDownload)
                            : await getMimeOperation({
                                  itemId: this.getItemId(attachment.model),
                              });

                        // Reset the attachmentType to ItemAttachment from ItemIdAttachment
                        attachType.__type = ITEM_ATTACHMENT_TYPE;
                        (<SmimeAttachmentType>attachType).MimeContent = mimeContent;
                        logUsage(
                            'SmimeAttachmentDownloadDatapoint',
                            { isMailDownload: true },
                            { isCore: true }
                        );
                    }
                } else if (attachmentType === TypeOfAttachment.File) {
                    if (!smimeAttachment.Content) {
                        const contentData = await fetchAttachmentContent(
                            attachmentIdToDownload,
                            true //asDataUri
                        );
                        // Reset the attachmentType to FileAttachment from ItemIdAttachment
                        attachType.__type = FILE_ATTACHMENT_TYPE;
                        (<SmimeAttachmentType>attachType).Content = removeDataHeaderInfoFromContent(
                            contentData
                        );
                        logUsage(
                            'SmimeAttachmentDownloadDatapoint',
                            { isFileDownload: true },
                            { isCore: true }
                        );
                    } else {
                        /**
                         * This occurs when we reply to a S/MIME mail with attachments.
                         * In this scenario, we already have the content for attachments
                         */
                        attachType = { ...smimeAttachment };
                    }
                } else if (attachmentType === TypeOfAttachment.Reference) {
                    throw new Error(UNSUPPORTED_REFERENCE_ATTACHMENT_ERROR_NAME);
                } else {
                    throw new Error(GENERIC_ERROR_NAME);
                }

                let smimeAttachmentId = smimeAttachment.AttachmentId as ClientAttachmentId;
                /**
                 * We create a new attachmentId for S/MIME attachments in the below 2 cases:
                 * 1. Toggle from non-S/MIME mode to S/MIME mode in compose.
                 *      Create new attachmentId since the attachment is now a S/MIME attachment.
                 * 2. Reply/ReplyAll/Forward a S/MIME mail with S/MIME admin/user settings turned on.
                 *      Create new attachmentId since the current attachmentId belongs to the S/MIME parent mail item(r/R/f/d) even though the id is of smimeAttachmentType.
                 *      The current attachmentId does not belong to the compose Item, hence we need to create a copy.
                 */
                if (
                    !isSmimeAttachmentType(smimeAttachment) ||
                    !isAttachmentPartOfItem(smimeAttachment, this.parentItemId)
                ) {
                    smimeAttachmentId = {
                        ...attachmentId({ Id: getIdWithSmimePrefix() }),
                        mailboxInfo: this.parentItemId.mailboxInfo,
                    };
                }
                /**
                 * This will handle the delete on successful download of attachments.
                 * This has to be done before the updateSmimeAttachment call because AttachmentId is updated after the call.
                 */
                onAttachmentDownloaded?.(attachment.model);

                updateSmimeAttachment(
                    attachmentState,
                    smimeAttachmentId,
                    smimeAttachment,
                    attachType,
                    this.parentItemId
                );
            } catch (e) {
                if (e.message !== UNSUPPORTED_REFERENCE_ATTACHMENT_ERROR_NAME) {
                    // If the error was due to being an unsupported reference file, no need to throw an
                    // unhandled exception since we know already why the attachment failed (VSO 52833)
                    trace.error('SmimeTraceError: ' + e.message);
                }

                const { IsInline } = this.currentDownloadItem.attachment.model;
                const { attachmentClass, type } = this.currentDownloadItem.attachment;
                logUsage(
                    'SmimeAttachmentDownloadDatapoint',
                    {
                        attachmentClass,
                        type,
                        IsInline,
                        isError: true,
                        message: e.message,
                    },
                    { isCore: true }
                );
                const attachmentDownloadItemFailure: AttachmentDownloadItemFailure = {
                    item: this.currentDownloadItem,
                    reason:
                        e.message === UNSUPPORTED_REFERENCE_ATTACHMENT_ERROR_NAME
                            ? AttachmentDownloadFailureReason.UnsupportedReferenceAttachment
                            : AttachmentDownloadFailureReason.GenericError,
                };
                this.downloadFailures.push(attachmentDownloadItemFailure);
            } finally {
                this.processNextAttachment();
            }
        } else {
            // If queue is empty, call finishProcess
            this.finishProcess();
        }
    }

    private finishProcess() {
        const attachmentId =
            this.currentDownloadItem && this.currentDownloadItem.attachmentState
                ? this.currentDownloadItem.attachmentState.attachmentId
                : null;

        trace.info('Attachment: SmimeAttachmentQueueManager: finishProcess');
        this.isInProgress = false;
        this.currentDownloadItem = null;
        this.onFinish(attachmentId);

        // Log performance datapoint
        this.datapoint.end();
        this.datapoint = null;

        this.downloadFailures.length &&
            this.removeFromAttachmentWellAndShowError(this.downloadFailures);
        this.downloadFailures = [];
    }

    private removeRelatedItemFromDownloadQueue(
        attachmentState: AttachmentState,
        container: SmimeAttachmentDownloadQueueItem[]
    ) {
        for (let i = 0; i < container.length; i++) {
            const item: SmimeAttachmentDownloadQueueItem = container[i];
            if (item.attachmentState.attachmentId === attachmentState.attachmentId) {
                container.splice(i, 1);
            }
        }
    }

    private removeFromAttachmentWellAndShowError(
        downloadFailures: AttachmentDownloadItemFailure[]
    ) {
        const referenceFailedAttachmentNames: string[] = [];
        const genericFailedAttachmentNames: string[] = [];

        //Remove from attachment well
        downloadFailures.map(downloadFailureItem => {
            const failedAttachmentName =
                downloadFailureItem.item.attachment &&
                downloadFailureItem.item.attachment.model.Name;

            if (
                downloadFailureItem.reason ===
                AttachmentDownloadFailureReason.UnsupportedReferenceAttachment
            ) {
                referenceFailedAttachmentNames.push(failedAttachmentName);
            } else if (
                downloadFailureItem.reason === AttachmentDownloadFailureReason.GenericError
            ) {
                genericFailedAttachmentNames.push(failedAttachmentName);
            }

            removeAttachmentFromWell(
                this.attachmentWellViewState,
                downloadFailureItem.item.attachmentState
            );
        });

        // Display an infobar, if there are any download failed attachments.
        if (referenceFailedAttachmentNames.length > 0) {
            this.infoBarAddHandler('ErrorSmimeHasCloudOrUriAttachments', [
                format(
                    loc(smimeComposeHasCloudAttachments),
                    referenceFailedAttachmentNames.join(', ')
                ),
            ]);
        }
        if (genericFailedAttachmentNames.length > 3) {
            this.infoBarAddHandler('ErrorAttachmentDownloadFailedMultipleFiles', [
                format(
                    loc(WarningAttachmentsCanNotBeDownloadedMultipleFiles),
                    genericFailedAttachmentNames.length
                ),
            ]);
        } else if (genericFailedAttachmentNames.length > 0) {
            this.infoBarAddHandler('ErrorAttachmentDownloadFailed', [
                format(
                    loc(WarningAttachmentsCanNotBeDownloaded),
                    genericFailedAttachmentNames.join(', ')
                ),
            ]);
        }
    }

    private getAttachmentId(
        attachmentType: AttachmentType,
        parentItemId: ClientItemId
    ): ClientAttachmentId {
        const itemIdAttachment = attachmentType as ItemIdAttachment;
        const attachmentIdToAttach =
            itemIdAttachment.AttachmentIdToAttach &&
            attachmentId({ Id: itemIdAttachment.AttachmentIdToAttach });
        let resultAttachmentId: AttachmentId;
        switch (attachmentType.__type) {
            // Even though the type is ItemAttachment, the internal object might still be an ItemIdAttachment
            case ITEM_ATTACHMENT_TYPE:
            case ITEM_ID_ATTACHMENT_TYPE:
                resultAttachmentId = attachmentIdToAttach;
                break;
            default:
                resultAttachmentId = attachmentIdToAttach || attachmentType.AttachmentId;
                break;
        }
        return {
            ...resultAttachmentId,
            mailboxInfo: parentItemId.mailboxInfo,
        };
    }

    private getItemId(attachmentType: AttachmentType): AttachmentId {
        const itemAttachment = attachmentType as ItemAttachment;
        return itemAttachment.Item
            ? itemAttachment.Item.ItemId
            : (<ItemIdAttachment>itemAttachment).ItemId;
    }
}
